import { NavLink } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { visibleRoutes } from '@/lib/routes';
import { cn } from '@/lib/cn';
import { useAuth } from '@/hooks/useAuth';

// Desktop-only sidebar (hidden under lg breakpoint).
// Mobile uses the bottom TabBar instead.
export function Sidebar() {
  const { user, role, logout } = useAuth();
  const routes = visibleRoutes(role);
  // Skip empty groups so a guest's sidebar doesn't render an empty "Operate" header.
  const groups = (['Today', 'Analyze', 'Operate'] as const)
    .filter((g) => routes.some((r) => r.group === g));
  const initials = (user ?? 'OP').slice(0, 2).toUpperCase();

  return (
    <aside className="hidden lg:flex flex-none w-60 bg-bg-2 border-r border-[color:var(--border)] flex-col py-6 px-3.5">
      <div className="px-3 mb-6 flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-lg bg-kam-grad shadow-glow-violet" />
        <div>
          <div className="text-sm font-bold tracking-tight">KAM</div>
          <div className="text-[10px] text-text-faint uppercase tracking-[0.14em]">Asset Manager</div>
        </div>
      </div>

      <nav className="flex-1 flex flex-col gap-5">
        {groups.map((group) => (
          <div key={group}>
            <div className="px-3 mb-2 label">{group}</div>
            <div className="flex flex-col gap-0.5">
              {routes.filter((r) => r.group === group).map((r) => (
                <NavLink
                  key={r.path}
                  to={r.path}
                  className={({ isActive }) =>
                    cn(
                      'group flex items-center gap-3 px-3 py-2 rounded-[10px] text-[13px] font-medium text-text-dim transition-colors',
                      'hover:bg-surface-2 hover:text-text',
                      isActive && 'bg-surface-2 text-text relative',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <span
                          aria-hidden
                          className="absolute -left-3.5 top-2 bottom-2 w-[3px] rounded-r bg-kam-grad"
                        />
                      )}
                      <r.icon size={16} strokeWidth={2} />
                      <span>{r.label}</span>
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="mt-4 px-3 py-2 rounded-[10px] bg-surface-2 border border-[color:var(--border)] flex items-center gap-2.5">
        <div className="w-7 h-7 rounded-md bg-surface-3 grid place-items-center text-[10px] font-bold text-text-dim">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold truncate">{user ?? 'Operator'}</div>
          <div className="text-[10px] text-text-faint truncate">paper trading</div>
        </div>
        <button
          type="button"
          onClick={() => void logout()}
          className="p-1.5 rounded-md text-text-faint hover:text-text hover:bg-surface-3 transition-colors"
          aria-label="Sign out"
          title="Sign out"
        >
          <LogOut size={14} />
        </button>
      </div>
    </aside>
  );
}
